// src/features/context/UserPreferencesPanel.tsx
// The signed-in admin's own Context Panel preferences — hidden sections,
// default rail tab, auto-open, and a raw-JSON editor for explicit section
// ordering (normally set by drag-and-drop in the panel itself).
// See docs/ux/context-ia.md section 4.

import { useEffect, useState } from 'react';
import { useI18n, useContext as useContextStore } from '@react/hooks';
import { Button, Input, Textarea, ErrorAlert } from '@react/components';
import { useScope } from '@/hooks/useScope';
import type { SectionOrder } from '@core/types/context';
import { tryParseJson } from './logic';

export function UserPreferencesPanel() {
  const { t } = useI18n();
  const store = useContextStore();
  const scope = useScope();
  const [hiddenSections, setHiddenSections] = useState('');
  const [defaultRailTab, setDefaultRailTab] = useState('');
  const [autoOpenRail, setAutoOpenRail] = useState(true);
  const [ordersText, setOrdersText] = useState('{}');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const tenantEntity = { type_id: '', type_name: 'tenant', id: scope.tenantId, platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: scope.productId };

  const load = async () => {
    setIsLoading(true);
    try {
      const r = await store.GetUserContextPreferences();
      setHiddenSections((r.preferences?.hidden_sections ?? []).join(', '));
      setDefaultRailTab(r.preferences?.default_rail_tab ?? '');
      setAutoOpenRail(r.preferences?.auto_open_rail ?? true);
      setOrdersText(JSON.stringify(r.preferences?.explicit_orders ?? {}, null, 2));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const { value: explicitOrders, error } = tryParseJson<Record<string, SectionOrder>>(ordersText);
    if (error) {
      setJsonError(error);
      return;
    }
    setJsonError(null);
    setIsSaving(true);
    try {
      await store.UpdateUserContextPreferences({
        entity: tenantEntity,
        preferences: {
          explicit_orders: explicitOrders,
          hidden_sections: hiddenSections.split(',').map((s) => s.trim()).filter(Boolean),
          default_rail_tab: defaultRailTab,
          auto_open_rail: autoOpenRail,
        },
      });
      load();
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;

  return (
    <form onSubmit={handleSave} className="space-y-3">
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      <Input label={t('context.userPreferences.hiddenSections')} value={hiddenSections} onChange={(e) => setHiddenSections(e.target.value)} helperText={t('context.userPreferences.hiddenSectionsHelp')} />
      <Input label={t('context.userPreferences.defaultRailTab')} value={defaultRailTab} onChange={(e) => setDefaultRailTab(e.target.value)} />
      <label className="flex items-center gap-2 cursor-pointer text-sm text-foreground">
        <input type="checkbox" checked={autoOpenRail} onChange={(e) => setAutoOpenRail(e.target.checked)} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
        {t('context.userPreferences.autoOpenRail')}
      </label>
      <Textarea
        label={t('context.userPreferences.ordersJson')}
        value={ordersText}
        onChange={(e) => setOrdersText(e.target.value)}
        rows={6}
        className="font-mono text-xs"
        helperText={t('context.userPreferences.ordersHelp')}
      />
      {jsonError && <p className="text-sm text-destructive">{jsonError}</p>}
      <Button type="submit" size="sm" disabled={isSaving}>{isSaving ? t('common.saving') : t('common.save')}</Button>
    </form>
  );
}
